/**
 * URL and taxonomy helpers for SEO property pages.
 *
 * This module is the single source of truth for translating between the
 * backend's enum values/names and the slugs used in public URLs. Keep route
 * shapes here so metadata, breadcrumbs, links, and sitemaps stay consistent.
 */
import type { Property } from "@/types/property";
import {
  CategoryPropertyTypeConfig,
  ListingType,
  PropertyCategory,
  PropertyTypeLabels,
  type PropertyType,
} from "@/types/property-config";

/**
 * Listing types that are allowed in canonical SEO URLs.
 * "buy" is intentionally excluded because it is an alias for "sale".
 */
export const CANONICAL_LISTING_TYPES = [
  ListingType.SALE,
  ListingType.RENT,
  ListingType.SHORTLET,
  ListingType.LEASE,
] as const;

/**
 * Human-readable labels for each canonical listing type.
 * Input: a ListingType enum value. Output: label used in headings/breadcrumbs.
 */
export const LISTING_TYPE_LABELS: Record<ListingType, string> = {
  [ListingType.SALE]: "For Sale",
  [ListingType.RENT]: "For Rent",
  [ListingType.SHORTLET]: "Shortlets",
  [ListingType.LEASE]: "For Lease",
};

/**
 * Short action words used by older/friendly URLs (for example "buy").
 * Input: a ListingType enum value. Output: action slug used in legacy routes.
 */
export const LISTING_TYPE_ACTIONS: Record<ListingType, string> = {
  [ListingType.SALE]: "buy",
  [ListingType.RENT]: "rent",
  [ListingType.SHORTLET]: "book",
  [ListingType.LEASE]: "lease",
};

/**
 * Maps each property category enum to its public URL slug.
 * Input: a PropertyCategory enum value. Output: URL-safe slug.
 */
export const CATEGORY_SLUGS: Record<PropertyCategory, string> = {
  [PropertyCategory.RESIDENTIAL]: "residential",
  [PropertyCategory.COMMERCIAL]: "commercial",
  [PropertyCategory.INDUSTRIAL]: "industrial",
  [PropertyCategory.LAND]: "land",
  [PropertyCategory.SPECIAL_PURPOSE]: "special-purpose",
};

/**
 * Maps each property category enum to its display label.
 * Input: a PropertyCategory enum value. Output: human-readable label.
 */
export const CATEGORY_LABELS: Record<PropertyCategory, string> = {
  [PropertyCategory.RESIDENTIAL]: "Residential",
  [PropertyCategory.COMMERCIAL]: "Commercial",
  [PropertyCategory.INDUSTRIAL]: "Industrial",
  [PropertyCategory.LAND]: "Land",
  [PropertyCategory.SPECIAL_PURPOSE]: "Special Purpose",
};

/**
 * Reverse lookup for CATEGORY_SLUGS.
 * Input: URL slug. Output: PropertyCategory enum, or undefined if unknown.
 */
const CATEGORY_BY_SLUG = new Map<string, PropertyCategory>(
  (Object.entries(CATEGORY_SLUGS) as [PropertyCategory, string][]).map(
    ([category, slug]) => [slug, category],
  ),
);

/**
 * Normalizes a URL/query value into a canonical ListingType.
 *
 * Input: any string such as "sale", "SALE", "buy", or an unknown value.
 * Output: a ListingType, or null when the value is missing/unsupported.
 * "buy" is normalized to "sale"; whitespace and casing are ignored.
 */
export function canonicalListingType(
  value?: string | null,
): ListingType | null {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "buy") return ListingType.SALE;
  return CANONICAL_LISTING_TYPES.includes(normalized as ListingType)
    ? (normalized as ListingType)
    : null;
}

/**
 * Converts a listing-type URL segment into a ListingType.
 *
 * Input: URL segment such as "sale" or "buy".
 * Output: canonical ListingType, or null if the segment is invalid.
 * This is an alias of canonicalListingType for call-site clarity.
 */
export function listingTypeFromSlug(
  value?: string | null,
): ListingType | null {
  return canonicalListingType(value);
}

/**
 * Converts a category URL slug into a PropertyCategory.
 *
 * Input: URL slug such as "residential" or "special-purpose".
 * Output: PropertyCategory enum, or null if the slug is unknown.
 */
export function categoryFromSlug(value?: string | null): PropertyCategory | null {
  return CATEGORY_BY_SLUG.get(String(value ?? "").toLowerCase()) ?? null;
}

/**
 * Converts a category enum/name into its URL slug.
 *
 * Input: enum value or string such as "RESIDENTIAL", "special purpose", or
 * "special-purpose".
 * Output: URL slug, or null if the value is not a known category.
 */
export function categorySlug(
  value?: string | null,
): string | null {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return CATEGORY_SLUGS[normalized as PropertyCategory] ?? null;
}

/**
 * Converts a category enum/name into a display label.
 *
 * Input: enum value or string such as "RESIDENTIAL" or "special-purpose".
 * Output: display label, or "Properties" when no category is provided/invalid.
 */
export function categoryLabel(value?: string | null): string {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return CATEGORY_LABELS[normalized as PropertyCategory] ?? "Properties";
}

/**
 * Converts a raw property type into a display label.
 *
 * Input: backend property type such as "DUPLEX" or "mini_flat".
 * Output: known label if available, otherwise a title-cased fallback.
 */
export function propertyTypeLabel(value?: string | null): string {
  if (!value) return "Property";
  return (
    PropertyTypeLabels[value as PropertyType] ??
    value
      .replace(/[-_]+/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
}

/**
 * Checks whether a property type is valid for a category.
 *
 * Input: a PropertyCategory and optional property type string.
 * Output: true when the type belongs to that category, otherwise false.
 */
export function isPropertyTypeInCategory(
  category: PropertyCategory,
  propertyType?: string | null,
): boolean {
  if (!propertyType) return false;
  return CategoryPropertyTypeConfig[category].includes(
    propertyType as PropertyType,
  );
}

/**
 * Converts a listing type into a display label.
 *
 * Input: listing-type string such as "sale" or "buy".
 * Output: label such as "For Sale", or "Properties" when invalid/missing.
 */
export function listingTypeLabel(value?: string | null): string {
  const listingType = canonicalListingType(value);
  return listingType ? LISTING_TYPE_LABELS[listingType] : "Properties";
}

/**
 * Converts arbitrary text into a URL-safe slug.
 *
 * Input: a name such as "Federal Capital Territory" or "Lagos & Ogun".
 * Output: lowercase hyphenated slug, or null when nothing usable remains.
 * Accents are removed, "&" becomes "and", and FCT is normalized to "abuja".
 */
function fallbackSlug(value?: string | null): string | null {
  if (!value) return null;
  const normalized = value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!normalized) return null;
  if (normalized === "federal-capital-territory" || normalized === "fct") {
    return "abuja";
  }
  return normalized;
}

/**
 * Converts a state name into its URL slug.
 *
 * Input: state name or slug.
 * Output: normalized slug, or "nigeria" when no value is provided.
 */
export function stateSlugFromName(value?: string | null): string {
  return fallbackSlug(value) || "nigeria";
}

/**
 * Resolves the state slug used in a property URL.
 *
 * Input: a partial Property.
 * Output: backend state_slug when present, otherwise a slug built from state.
 * Returns null only when neither value can produce a slug.
 */
export function propertyStateSlug(
  property: Partial<Property>,
): string | null {
  return property.state_slug || fallbackSlug(property.state);
}

/**
 * Resolves the city slug used in a property URL.
 *
 * Input: a partial Property.
 * Output: backend city_slug, or null when missing.
 * There is intentionally no name-based fallback: the frontend should not
 * invent a city URL that the backend cannot filter by.
 */
export function propertyCitySlug(property: Partial<Property>): string | null {
  return property.city_slug || null;
}

/**
 * Input shape for buildListingPath.
 * All fields are optional so callers can build a path at any hierarchy level:
 * listing type -> category -> property type -> state -> city.
 */
export interface ListingPathInput {
  listingType?: string | null;
  category?: string | null;
  propertyType?: string | null;
  stateSlug?: string | null;
  citySlug?: string | null;
}

/**
 * Builds a canonical listing or location path.
 *
 * Input: optional listing type, category, property type, state slug, and city
 * slug.
 * Output: a path beginning with "/properties".
 *
 * Examples:
 * - no listing type + state/city -> /properties/in/lagos/ikeja
 * - listing type only          -> /properties/sale
 * - category + location        -> /properties/sale/residential/in/lagos/ikeja
 * - property type is only used when a category is also present.
 */
export function buildListingPath({
  listingType,
  category,
  propertyType,
  stateSlug,
  citySlug,
}: ListingPathInput): string {
  const type = canonicalListingType(listingType);
  const categoryPart = categorySlug(category);

  // No valid listing type: treat this as a pure location page.
  if (!type) {
    if (stateSlug) {
      return citySlug
        ? `/properties/in/${stateSlug}/${citySlug}`
        : `/properties/in/${stateSlug}`;
    }
    return "/properties";
  }

  // Listing type without a category: optionally add location.
  if (!categoryPart) {
    if (stateSlug) {
      return citySlug
        ? `/properties/${type}/in/${stateSlug}/${citySlug}`
        : `/properties/${type}/in/${stateSlug}`;
    }
    return `/properties/${type}`;
  }

  // Full taxonomy: property type and location are appended only after category.
  const parts = ["properties", type, categoryPart];
  if (propertyType) {
    parts.push(propertyType);
    if (stateSlug) {
      parts.push(stateSlug);
      if (citySlug) parts.push(citySlug);
    }
  } else if (stateSlug) {
    parts.push("in", stateSlug);
    if (citySlug) parts.push(citySlug);
  }
  return `/${parts.join("/")}`;
}

/**
 * Builds the canonical URL for a property detail page.
 *
 * Input: a partial Property with at least slug or id.
 * Output: the deeply nested SEO path when listing type, category, property
 * type, state, and city are all present and valid; otherwise the stable
 * /property/{identifier} fallback.
 */
export function buildPropertyDetailPath(
  property: Partial<Property> & { id?: string; slug?: string },
): string {
  const identifier = property.slug || property.id;
  if (!identifier) return "/properties";

  const type = canonicalListingType(property.listing_type);
  const categoryPart = categorySlug(property.property_category);
  const state = propertyStateSlug(property);
  const city = propertyCitySlug(property);

  if (
    type &&
    categoryPart &&
    property.property_type &&
    isPropertyTypeInCategory(
      categoryFromSlug(categoryPart) as PropertyCategory,
      property.property_type,
    ) &&
    state &&
    city
  ) {
    return `/properties/${type}/${categoryPart}/${property.property_type}/${state}/${city}/${identifier}`;
  }

  return `/property/${identifier}`;
}

/**
 * Builds a location page path.
 *
 * Input: required state slug and optional city slug.
 * Output: /properties/in/{state} or /properties/in/{state}/{city}.
 */
export function buildLocationPath(
  stateSlug: string,
  citySlug?: string | null,
): string {
  return citySlug
    ? `/properties/in/${stateSlug}/${citySlug}`
    : `/properties/in/${stateSlug}`;
}

/**
 * Builds the breadcrumb trail for a listing or location page.
 *
 * Input: optional listing type, category, property type, and state/city names
 * plus slugs. Names are used for display; slugs are used to build hrefs.
 * Output: ordered array of { name, href } breadcrumb items, always starting
 * with Home and Properties.
 */
export function buildListingBreadcrumbs(input: {
  listingType?: string | null;
  category?: string | null;
  propertyType?: string | null;
  stateName?: string | null;
  stateSlug?: string | null;
  cityName?: string | null;
  citySlug?: string | null;
}): { name: string; href: string }[] {
  const crumbs: { name: string; href: string }[] = [
    { name: "Home", href: "/" },
    { name: "Properties", href: "/properties" },
  ];

  // Location-only pages (no listing type): Home > Properties > State > City.
  if (!input.listingType) {
    if (input.stateSlug) {
      crumbs.push({
        name: input.stateName || input.stateSlug,
        href: buildLocationPath(input.stateSlug),
      });
      if (input.citySlug) {
        crumbs.push({
          name: input.cityName || input.citySlug,
          href: buildLocationPath(input.stateSlug, input.citySlug),
        });
      }
    }
    return crumbs;
  }

  // Listing pages: append each taxonomy level that was supplied.
  crumbs.push({
    name: listingTypeLabel(input.listingType),
    href: buildListingPath({ listingType: input.listingType }),
  });

  if (input.category) {
    crumbs.push({
      name: categoryLabel(input.category),
      href: buildListingPath({
        listingType: input.listingType,
        category: input.category,
      }),
    });
    if (input.propertyType) {
      crumbs.push({
        name: propertyTypeLabel(input.propertyType),
        href: buildListingPath({
          listingType: input.listingType,
          category: input.category,
          propertyType: input.propertyType,
        }),
      });
    }
    if (input.stateSlug) {
      crumbs.push({
        name: input.stateName || input.stateSlug,
        href: buildListingPath({
          listingType: input.listingType,
          category: input.category,
          propertyType: input.propertyType,
          stateSlug: input.stateSlug,
        }),
      });
      if (input.citySlug) {
        crumbs.push({
          name: input.cityName || input.citySlug,
          href: buildListingPath({
            listingType: input.listingType,
            category: input.category,
            propertyType: input.propertyType,
            stateSlug: input.stateSlug,
            citySlug: input.citySlug,
          }),
        });
      }
    }
  }

  return crumbs;
}
